import { useTranslation } from 'react-i18next'
import EmptyState from '../../../components/ui/EmptyState'
import SectionCard from '../../../components/ui/SectionCard'
import { formatPercent } from '../../../lib/number'
import type { ManagerDelay } from '../types'

interface Props {
  rows: ManagerDelay[]
}

/**
 * **"En Fazla Gecikme Yaşayan İlk 3 Yönetici"** (086 — kullanıcı isteği 2026-08-13 + mockup).
 *
 * <p>Her satır: sıra → ad → çubuk → "N Aksiyon (%oran)". <b>Geciken</b>, hedef teslim tarihi geçmiş
 * aksiyondur; iş sonradan bitmiş olsa da termin kaçırılmıştır. Bu tanım "Durumlara Göre Detaylı
 * Dağılım"daki üç <b>Gecikmiş</b> sütununun tanımıyla aynıdır → kartın sayıları o grafikle çelişemez.
 *
 * <p><b>Yüzdenin paydası panonun TOPLAM aksiyon sayısıdır</b> (kullanıcı kararı 2026-08-13), yöneticinin
 * kendi toplamı değil — üç satır aynı ölçeğe oturur. Sunucu hesaplar; istemci ikinci bir payda tutmaz.
 *
 * <p><b>Çubuk ölçeği ayrıdır:</b> en üstteki satır çubuğu doldurur, diğerleri ona oranlanır. Yüzdeyi
 * çubuk boyuna çevirmek küçük oranlarda (%2 gibi) görünmez çubuklar üretirdi; sayı zaten satırda yazılı.
 *
 * <p>Renk (kırmızı) tek taşıyıcı değildir: adet ve oran metin olarak da satırdadır.
 *
 * <p><b>Düzen (2026-08-17 mockup `cards.png`):</b> her yönetici kendi <b>kutucuğuna</b> alınır ve iki
 * satır olur — üstte sıra + ad + adet, altta tam genişlikte çubuk ve <b>ucuna yazılan oran</b>. Oran
 * artık çubuğun içinde metin olduğu için ray {@code role="img"} taşımaz: iki sayı da görünür metindir,
 * ekran okuyucu ad → adet → oran sırasıyla okur (ikinci bir aria özeti aynı bilgiyi yinelerdi).
 */
export default function TopDelayedManagersCard({ rows }: Readonly<Props>) {
  const { t, i18n } = useTranslation()
  const title = t('home.topDelayed.title')

  if (rows.length === 0) {
    return (
      <SectionCard title={title} hint={t('home.topDelayed.hint')}>
        <EmptyState
          icon="pi pi-check-circle"
          title={t('home.topDelayed.emptyTitle')}
          description={t('home.topDelayed.emptyDesc')}
        />
      </SectionCard>
    )
  }

  const max = rows.reduce((acc, r) => Math.max(acc, r.lateCount), 0)

  return (
    <SectionCard title={title} hint={t('home.topDelayed.hint')} className="home-delay">
      <ol className="home-delay-list">
        {rows.map((r, index) => (
          <li key={r.employeeId} className="home-delay-item">
            <div className="home-delay-head">
              <span className="home-delay-rank">{index + 1}</span>
              <span className="home-delay-name" title={r.jobTitle ?? undefined}>
                {r.fullName}
              </span>
              <span className="home-delay-count">
                {t('home.topDelayed.actionCount', { count: r.lateCount })}
              </span>
            </div>
            {/* Ray, çubuk (oranın kendisi) ve oran yazısını YAN YANA tutar: yazı çubuğun bittiği yerde
                durur → hangi çubuğun hangi oran olduğu, ayrı bir sütuna bakmadan okunur. */}
            <span className="home-delay-track">
              <span
                className="home-delay-bar"
                style={{ width: `${max > 0 ? (r.lateCount * 100) / max : 0}%` }}
              />
              <span className="home-delay-percent">
                {t('home.topDelayed.percentValue', {
                  percent: formatPercent(r.percent, i18n.language),
                })}
              </span>
            </span>
          </li>
        ))}
      </ol>
      <p className="home-brk-note">
        <i className="pi pi-info-circle" aria-hidden /> {t('home.topDelayed.note')}
      </p>
    </SectionCard>
  )
}
